import React, { PureComponent } from "react";
import clsx from "clsx";
import Uri from "jsuri";

import {
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  CartesianGrid,
  Cell,
} from "recharts";

import {
  selectFormat,
  MAIN_COLORS,
  CustomizedYAxisTick,
  StandardTooltip,
  Error,
  customColorIndex,
  formatDateFromData,
  getAllKeys,
} from "components/Utils/Utils.js";

import { closeAllBarchartTooltips } from "components/Utils/InsightsUtils.js";

import { XRayTooltip } from "components/Utils/XrayUtils.js";

import { getXrayTooltipPosition, findParentNodeByClass } from "helpers/DOM";

class PocketWatchViewingWeeklyChart extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.node = React.createRef();
    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    // close all tooltips while resizing the screen
    closeAllBarchartTooltips();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef && this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  colorsIndex = () => new Uri(this.props.url).getQueryParamValue("colors");

  renderBars = (data) => {
    let bars = [];
    let labels = [];
    const sortedKeys = this.getSortedKeys(data);
    const isFixedBarWidth = data.length <= 7;

    [...new Set(sortedKeys)].forEach((bar, i) => {
      bars.push(
        <Bar
          key={i}
          dataKey={bar}
          stackId={"stack"}
          barSize={isFixedBarWidth ? 35 : null}
          fill={MAIN_COLORS[customColorIndex(i, this.colorsIndex())]}
          isAnimationActive={false}
          // only first bar needs background
          cursor="pointer"
          onClick={(d) => {
            const label = labels.filter((l) => l.date === d.date);
            const extra = sortedKeys
              .map((k, j) => ({
                key: k,
                value: d[k],
                color: MAIN_COLORS[customColorIndex(j, this.colorsIndex())],
              }))
              .filter((o) => o.value !== undefined);

            d.extra = extra;
            d.insight = label.length ? label[0] : null;

            this.clickBar(d, bar);
          }}
        >
          {data.map(
            (entry, index) =>
              entry && (
                <Cell
                  key={`cell-${bar}-${index}`}
                  fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                ></Cell>
              )
          )}
        </Bar>
      );
    });

    return bars;
  };

  clickBar(d, dataKey) {
    if (this.state.tooltip.show && d.date === this.state.tooltip.date) {
      this.setState({
        tooltip: { ...d, show: false, dataKey },
      });
    } else {
      // close all tooltips from this and other widgets as well
      closeAllBarchartTooltips();
      this.setState({
        tooltip: { ...d, show: true, dataKey },
      });
    }
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    // close all tooltips when the user clicks outside the context
    if (
      !e.target.classList.contains("recharts-rectangle") &&
      // and the parent isn't a xray tooltip
      !findParentNodeByClass(e.target, "xray-tooltip-container")
    )
      closeAllBarchartTooltips();
  };

  getSortedKeys(_data) {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);
    const keys = getAllKeys(data, ["date"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  }

  render() {
    const { data } = this.props;

    const widget_data = data.chart;

    if (data.hasData === false) {
      return <Error minHeight={360} />;
    }

    let preparedData = formatDateFromData(widget_data, "YYYY/MM/DD");
    return (
      <>
        <div ref={this.divPosition}>
          <div className="d-flex mt-2">
            <ResponsiveContainer height={370} width="100%">
              <BarChart
                data={preparedData}
                margin={{ top: 8, right: 0, bottom: 15, left: -38 }}
                stackOffset="sign"
              >
                <CartesianGrid
                  stroke="#FEE5DE"
                  strokeDasharray="3 3"
                  vertical={false}
                />

                {!this.state.tooltip.show && (
                  <Tooltip
                    cursor={{ fill: "#eee", opacity: 0.5 }}
                    content={
                      <StandardTooltip
                        render={({ data }) => {
                          const payload = data(this.props);

                          if (payload !== null) {
                            const tooltip_data = payload[0].payload;

                            let total = "";

                            if (tooltip_data.total) {
                              total = tooltip_data.total;
                            } else {
                              total = payload.reduce(
                                (acc, cur) => acc + (cur.value || 0),
                                0
                              );
                            }

                            const topContent = tooltip_data.topContent;

                            if (topContent) {
                              topContent.value = topContent.views;
                              topContent.trends.forEach((chartData) => {
                                chartData.value = chartData.views;
                                chartData.label = chartData.date;
                                // eslint-disable-next-line no-self-assign
                                chartData.selected = chartData.selected;
                              });
                            }

                            return (
                              <div className="standard-tooltip">
                                {tooltip_data.date && (
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Date:
                                    </span>
                                    <span className="standard-tooltip__value-header">
                                      {tooltip_data.date}
                                    </span>
                                  </header>
                                )}
                                <main className="standard-tooltip__main">
                                  {Object.keys(payload)
                                    .sort(
                                      (a, b) =>
                                        payload[b].value - payload[a].value
                                    )
                                    .map((key, index) => (
                                      <div
                                        className="standard-tooltip__item"
                                        key={key}
                                      >
                                        {payload[key].dataKey !== "insights" ? (
                                          <>
                                            <span
                                              className="standard-tooltip__icon"
                                              style={{
                                                background: payload[key].color,
                                              }}
                                            ></span>
                                            <span
                                              className={clsx(
                                                "standard-tooltip__key",
                                                "text-capitalize"
                                              )}
                                              style={{
                                                color: payload[key].color,
                                              }}
                                            >
                                              {payload[key].dataKey}{" "}
                                            </span>

                                            <span className="standard-tooltip__value">
                                              {selectFormat(
                                                "number",
                                                payload[key].value
                                              )}
                                            </span>
                                          </>
                                        ) : null}
                                      </div>
                                    ))}

                                  <div className="standard-tooltip__dilimiter1"></div>

                                  <div className="standard-tooltip__item">
                                    <span
                                      className={clsx(
                                        "standard-tooltip__key",
                                        "text-capitalize",
                                        "total"
                                      )}
                                    >
                                      Total{" "}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat("number", total)}
                                    </span>
                                  </div>
                                </main>
                              </div>
                            );
                          }
                        }}
                      />
                    }
                  />
                )}
                {this.renderBars(preparedData)}
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  scale="auto"
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(d) => selectFormat("number", d)}
                  tick={<CustomizedYAxisTick />}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div
            className="xray-tooltip-container"
            style={{
              top: "5.4rem", //(this.state.tooltip.y - 20) + 'px',
              left:
                this.state.tooltip.x + this.state.tooltip.width + 220 <
                this.state.width
                  ? this.state.tooltip.x + this.state.tooltip.width + 15 + "px"
                  : this.state.tooltip.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        </div>
      </>
    );
  }
}
export default PocketWatchViewingWeeklyChart;
